import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Cloud, Cpu, ShieldCheck, Workflow } from "lucide-react";
import Marquee from "@/components/Marquee";
import HeroVisual from "@/components/HeroVisual";
import SolutionMotif from "@/components/SolutionMotif";
import GearMark from "@/components/GearMark";
import { MaskedLine, ParallaxY, Reveal, SectionHeader, Tilt } from "@/components/Reveal";
import { CTABand, PrimaryCTA, SecondaryCTA } from "@/components/CTA";
import { APPS } from "@/config";
import usePageMeta from "@/hooks/usePageMeta";

const SOLUTION_COPY = {
  [APPS[0].id]: {
    tag: "For channel partner sales management",
    desc: `${APPS[0].shortName} sends your buyer a page branded as your own when the deal is agreed and ready to purchase. The buyer picks the AWS Marketplace benefits open to them — committed spend, standard contract, flexible payments — and the deal moves.`,
    features: [
      "A buyer page branded as your own",
      "Buyer picks their Marketplace benefits",
      `Hands off to ${APPS[1].shortName} to build the offer`,
    ],
    motif: "amplify",
  },
  [APPS[1].id]: {
    tag: "For AWS alliances leaders at ISVs",
    desc: `${APPS[1].shortName} turns an agreed deal into an accurate private offer: the agent matches the incentive playbook, drafts the offer and authorization, and stops for your approval — then tracks it to acceptance.`,
    features: [
      "Offers built right the first time",
      "A human approval gate before release",
      "One timeline for every deal",
    ],
    motif: "sync",
  },
};

const SOLUTIONS = APPS.map((a, i) => ({
  ...a,
  index: `0${i + 1}`,
  testid: `solution-card-${a.id}`,
  ...SOLUTION_COPY[a.id],
}));

const BENTO = [
  {
    icon: Cpu,
    index: "01",
    title: "AI-native, not bolted on",
    copy: "Transactr reads the signals across marketplace listings, partner accounts, and CRM history the way a strong overlay reads a deal — and turns them into actions your sellers can run the same day.",
    span: "lg:col-span-2",
    diagram: true,
  },
  {
    icon: Workflow,
    index: "02",
    title: "Autonomous by design",
    copy: "Detection, briefs, offer drafts, and follow-ups fire on their own — your team reviews, approves, and sells.",
  },
  {
    icon: Cloud,
    index: "03",
    title: "Marketplace-native",
    copy: "Listings, private offers, ACE, and co-sell events are first-class objects in the data model — not CSV exports.",
  },
  {
    icon: ShieldCheck,
    index: "04",
    title: "Partner-grade trust",
    copy: "Permissioning and audit trails are built in from day one, so security review is a formality, not a project.",
    span: "lg:col-span-2",
    footnotes: ["Role-based permissions", "Every sync logged", "Explainable numbers"],
  },
];

const PERSONAS = [
  {
    testid: "persona-panel-partner-sales",
    chip: "For channel partner sales management",
    title: "Give every buyer a reason to say yes on the Marketplace.",
    copy: `When a deal is agreed, your sellers send a page branded as their own. Buyers see the benefits open to them, pick the ones they want, and the internal approvals start moving — while the offer builds itself behind the scenes.`,
    bullets: [
      "Larger deals with committed spend and payment options",
      "Faster closes on the standard contract",
      "A motion every rep can run, not just top sellers",
    ],
    link: {
      to: APPS[0].path,
      label: `Explore ${APPS[0].shortName}`,
      testid: `persona-link-${APPS[0].id}`,
    },
  },
  {
    testid: "persona-panel-alliances",
    chip: "For AWS alliances leaders at ISVs",
    title: "Every private offer drafted right — and fully tracked.",
    copy: `${APPS[1].shortName} turns agreed deals into accurate private offers: the agent matches the playbook, drafts the offer and authorization, and stops for your approval — then tracks it to acceptance with a signed audit trail.`,
    bullets: [
      "Offers built right the first time",
      "A human approval gate before anything is released",
      "One timeline for every deal",
    ],
    link: {
      to: APPS[1].path,
      label: `Explore ${APPS[1].shortName}`,
      testid: `persona-link-${APPS[1].id}`,
    },
  },
];

function SolutionRow({ s, flip }) {
  return (
    <Reveal>
      <article
        data-testid={s.testid}
        className="group grid overflow-hidden rounded-[1.75rem] border border-line bg-white transition-all duration-500 hover:border-vermillion/40 hover:shadow-[0_40px_80px_-50px_rgba(214,53,10,0.5)] lg:grid-cols-2"
      >
        <div className={`relative ${flip ? "lg:order-2" : ""}`}>
          <SolutionMotif variant={s.motif} />
        </div>
        <div className="relative flex flex-col justify-center p-8 lg:p-12">
          <span className="absolute left-0 top-10 h-[2px] w-0 bg-vermillion transition-all duration-700 group-hover:w-24" />
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-inksoft">
            Solution {s.index} — {s.id}
          </p>
          <h3 className="mt-4 font-display text-3xl font-bold tracking-[-0.02em] text-ink lg:text-4xl">
            {s.name}
          </h3>
          <p className="mt-3 inline-flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-vermillion">
            <span className="h-1.5 w-1.5 rotate-45 bg-vermillion" />
            {s.tag}
          </p>
          <p className="mt-5 leading-relaxed text-inksoft">{s.desc}</p>
          <ul className="mt-7">
            {s.features.map((f, i) => (
              <li
                key={f}
                className={`flex items-center gap-3 py-3 text-sm text-ink ${
                  i > 0 ? "border-t border-line" : ""
                }`}
              >
                <span className="font-mono text-[10px] text-vermillion">0{i + 1}</span>
                {f}
              </li>
            ))}
          </ul>
          <Link
            to={s.path}
            data-testid={`solution-link-${s.id}`}
            className="group/link mt-8 inline-flex w-fit items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-vermillion"
          >
            {`Explore ${s.shortName}`}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover/link:translate-x-1" />
          </Link>
        </div>
      </article>
    </Reveal>
  );
}

export default function Home() {
  usePageMeta(
    "Transactr — Autonomous Workflows for Upselling in the AWS Marketplace",
    "Transactr turns agreed deals into AWS Marketplace purchases: a buyer page branded as your own, then private offers on autopilot. Built for channel partners, ISVs, distributors, and brokers."
  );
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-60" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(214,53,10,0.06),transparent_55%)]" />
        <div className="container-t relative grid gap-14 pb-20 pt-32 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:pb-28 lg:pt-40">
          <div>
            <Reveal delay={0} y={14}>
              <p className="eyebrow" data-testid="hero-eyebrow">
                <span className="eyebrow-mark" />
                AI-native tooling for AWS marketplace teams
              </p>
            </Reveal>
            <h1 className="mt-7 font-display text-[clamp(2.2rem,3.8vw,3.4rem)] font-extrabold leading-[1.08] tracking-[-0.02em] text-ink">
              <MaskedLine delay={0.08}>Autonomous workflows</MaskedLine>
              <MaskedLine delay={0.2}>
                for <em className="font-accent text-[1.2em] font-medium italic tracking-normal text-vermillion">upselling</em> in the
              </MaskedLine>
              <MaskedLine delay={0.32}>AWS Marketplace.</MaskedLine>
            </h1>
            <Reveal delay={0.55} y={18} className="mt-7">
              <p className="max-w-xl text-base leading-relaxed text-inksoft lg:text-lg">
                Transactr turns marketplace data, partner signals, and CRM records into self-running
                co-sell motion — so channel and alliance teams spend their time closing, not reconciling.
              </p>
            </Reveal>
            <Reveal delay={0.68} y={18} className="mt-10 flex flex-wrap items-center gap-4">
              <PrimaryCTA testid="hero-cta-get-a-demo-btn" />
              <SecondaryCTA testid="hero-cta-book-consultation-btn" />
            </Reveal>
            <Reveal delay={0.8} y={14} className="mt-14">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-line pt-6">
                {["Built for AWS Marketplace", "CRM-native", "Partner-ready"].map((c) => (
                  <span
                    key={c}
                    className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft"
                  >
                    <span className="h-1.5 w-1.5 rotate-45 bg-vermillion" />
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <Tilt max={3.5}>
            <ParallaxY from={18} to={-18}>
              <HeroVisual />
            </ParallaxY>
          </Tilt>
        </div>
      </section>

      <Marquee />

      {/* SOLUTIONS */}
      <section className="container-t py-24 lg:py-32" data-testid="home-solutions-section">
        <SectionHeader
          eyebrow="Solutions"
          title="Two products. One autonomous revenue motion."
          sub="Purpose-built for the people who carry channel and alliance numbers — and positioned to run the upsell motion for them."
        />
        <div className="mt-14 space-y-8">
          {SOLUTIONS.map((s, i) => (
            <SolutionRow key={s.id} s={s} flip={i % 2 === 1} />
          ))}
        </div>
      </section>

      {/* WHY BENTO */}
      <section className="bg-parchment/60 py-24 lg:py-32" data-testid="home-why-section">
        <div className="container-t">
          <SectionHeader
            eyebrow="Why Transactr"
            title="Built like infrastructure, not like a dashboard."
            sub="Dashboards tell you what happened. Transactr does something about it — continuously, across the systems your revenue already lives in."
          />
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {BENTO.map((b, i) => (
              <Reveal key={b.index} delay={i * 0.08} className={`${b.span || ""} h-full`}>
                <div className="flex h-full flex-col rounded-3xl border border-line bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-40px_rgba(31,28,25,0.35)] lg:p-10">
                  <div className="flex items-center justify-between">
                    <b.icon size={22} strokeWidth={1.6} className="text-vermillion" />
                    <span className="font-mono text-[11px] text-inksoft/60">{b.index}</span>
                  </div>
                  <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-ink lg:text-2xl">
                    {b.title}
                  </h3>
                  <p className="mt-3.5 leading-relaxed text-inksoft">{b.copy}</p>
                  {b.diagram ? (
                    <div className="mt-auto flex flex-wrap items-center gap-3 border-t border-line pt-7 lg:mt-9">
                      {["Marketplace data", "Partner signals"].map((t) => (
                        <span
                          key={t}
                          className="rounded-full border border-line bg-paper px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-inksoft"
                        >
                          {t}
                        </span>
                      ))}
                      <span className="h-px w-8 bg-bluerule" />
                      <GearMark size={20} className="animate-spin-slow" />
                      <span className="rounded-full border border-vermillion/30 bg-vermillion-tint px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-vermillion">
                        Next action
                      </span>
                    </div>
                  ) : null}
                  {b.footnotes ? (
                    <div className="mt-auto flex flex-wrap gap-x-6 gap-y-2 border-t border-line pt-7 lg:mt-9">
                      {b.footnotes.map((f) => (
                        <span key={f} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-inksoft">
                          <span className="h-1 w-1 rounded-full bg-vermillion" />
                          {f}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="container-t py-24 lg:py-32" data-testid="home-personas-section">
        <SectionHeader
          eyebrow="Who it's for"
          title="Built for the two seats that carry the number."
          sub="If you manage the channel or run the AWS alliance at an ISV, the upsell motion in your marketplace book is the growth you haven't automated yet."
        />
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {PERSONAS.map((p, i) => (
            <Reveal key={p.testid} delay={i * 0.1} className="h-full">
              <div
                data-testid={p.testid}
                className="group flex h-full flex-col rounded-3xl border border-line bg-white p-8 transition-all duration-500 hover:border-ink/30 lg:p-10"
              >
                <div className="h-px w-full bg-bluerule/70 transition-all duration-500 group-hover:bg-vermillion" />
                <p className="mt-8 inline-flex w-fit items-center gap-2.5 rounded-full border border-line bg-paper px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-inksoft">
                  <span className="h-1.5 w-1.5 rotate-45 bg-vermillion" />
                  {p.chip}
                </p>
                <h3 className="mt-6 font-display text-2xl font-bold leading-snug tracking-[-0.02em] text-ink lg:text-[1.7rem]">
                  {p.title}
                </h3>
                <p className="mt-4 leading-relaxed text-inksoft">{p.copy}</p>
                <ul className="mt-7 border-t border-line">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-3 border-b border-line py-3.5 text-sm text-ink">
                      <ArrowUpRight size={14} className="shrink-0 text-vermillion" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.link.to}
                  data-testid={p.link.testid}
                  className="group/link mt-8 inline-flex w-fit items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-vermillion"
                >
                  {p.link.label}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}
